import {ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Router} from '@angular/router';
import {FormBuilder, FormGroup} from '@angular/forms';
import {ModalController, PopoverController} from '@ionic/angular';
import {AccountPaymentFavoriteDTO, AccountPaymentFavoriteService, TypeEnum} from '@wlp/ui-bs-account-payment-favorite';
import {BilletLotService, BilletService, ChargingService} from '@wlp/ui-bs-cashin';
import {UserInfoService} from '@wlp/ui-bs-login';
import {PayerDto} from '@wlp/ui-px-cashin';
import * as moment from 'moment';
import {UserInfoProfile} from '@wlp/ui-bs-login/lib/dto/user-info-profile';
import {catchError, retry, tap} from 'rxjs/operators';
import {of} from 'rxjs';
import {FavoritesDataFilterPipe} from '../../../../favorites-data-filter.pipe';
import {LoadingService} from 'src/app/core/layout/services/loading.service';

type SwitchFavoritesOption = 'new' | 'favorites'

@Component({
  selector: 'ui-t-form-payer',
  templateUrl: './form-payer.html',
  styleUrls: ['./form-payer.scss'],
  providers: [FavoritesDataFilterPipe],
})
export class FormPayer implements OnInit {

  @Input() public payer: PayerDto;
  @Output() public submitForm = new EventEmitter<string>();
  @Output() public onCancel = new EventEmitter<string>();
  public userProfile: UserInfoProfile;
  public payerForm: FormGroup;
  public errorMessages: any;
  public listUf: Array<any>;

  public switchOptions: SwitchFavoritesOption;
  public switchFavorites: SwitchFavoritesOption = 'new';

  public listFavorites: any;
  public listFavoritesFiltered: any;

  public selectedFavorite: any = null;
  public saveToFavorites = false;

  constructor(
    private formBuilder: FormBuilder,
    public modalController: ModalController,
    public popoverController: PopoverController,
    public router: Router,
    protected userInfoService: UserInfoService,
    public chargingService: ChargingService,
    public billetService: BilletService,
    public billetLotService: BilletLotService,
    public accountPaymentFavoriteService: AccountPaymentFavoriteService,
    private cd: ChangeDetectorRef,
    public pipeFilterFavorites: FavoritesDataFilterPipe,
    private loadingService: LoadingService,
  ) {
    moment.locale('pt-br');
  }

  public ngOnInit() {
    this.errorMessages = this.accountPaymentFavoriteService.getFormMessageError();
    this.listUf = this.accountPaymentFavoriteService.getUf();
    this.createPayerForm();
    this.loadUser().then(() => this.getAllFavorites());
  }

  public async loadUser() {
    try {
      this.userProfile = await this.userInfoService.getInfoProfile();
    } catch (e) {
      console.error('Failed to load user details', e);
    }
  }

  public createPayerForm() {
    this.payerForm = this.accountPaymentFavoriteService.createFormFavoriteCharging();
    this.payerForm.setValue({
      document: this.payer.document,
      name: this.payer.name,
      email: this.payer.email,
      postalCode: this.payer.address.zipCode,
      address: this.payer.address.street,
      numberAddress: this.payer.address.streetNumber,
      complementAddress: this.payer.address.streetComplement,
      neighborhood: this.payer.address.neighborhood,
      cityAddress: this.payer.address.city,
      uf: this.payer.address.state,
      country: this.payer.address.country,
    });

    this.selectedFavorite = this.payerForm.value;
    this.loadingService.hide();
  }

  public searchPostalCode(data: any) {
    this.accountPaymentFavoriteService.searchPostaCode(data.target.value);
  }

  public getAllFavorites() {
    this.accountPaymentFavoriteService
      .getAllFavoritesType(this.userProfile.uuidWhiteLabel, this.userProfile.uuidAccount, TypeEnum.BILLET)
      .pipe(
        tap((data) => {
          this.listFavorites = data;
          this.listFavoritesFiltered = this.accountPaymentFavoriteService.sortFavoritesListByName(this.listFavorites);
        }),
        retry(2),
        catchError((_) => {
          return of(null);
        }),
      )
      .subscribe();
  }

  public filterReceipt(value) {
    if (value) {
      this.listFavoritesFiltered = this.pipeFilterFavorites.transform(this.listFavorites, value);
    } else {
      this.listFavoritesFiltered = this.listFavorites;
    }
  }

  public handleFavoriteSelected(favorite) {
    this.payerForm.setValue({
      document: favorite.document,
      name: favorite.name,
      email: favorite.email,
      postalCode: favorite.zipCode,
      address: favorite.address,
      numberAddress: favorite.addressNumber,
      complementAddress: favorite.addressComplement,
      neighborhood: favorite.neighborhood,
      cityAddress: favorite.city,
      uf: favorite.state,
      country: favorite.country,
    });

    this.selectedFavorite = this.payerForm.value;
    this.saveToFavorites = false;
    this.switchFavorites = 'new';
  }

  public payerHasUpdated(): boolean {
    const formValue = this.payerForm.value;
    const initialPayer = {
      document: this.payer.document,
      email: this.payer.email,
      name: this.payer.name,
      address: this.payer.address.street,
      cityAddress: this.payer.address.city,
      complementAddress: this.payer.address.streetComplement,
      country: this.payer.address.country,
      neighborhood: this.payer.address.neighborhood,
      numberAddress: this.payer.address.streetNumber,
      postalCode: this.payer.address.zipCode,
      uf: this.payer.address.state,
    };

    let formHasChanged = false;
    Object.keys(initialPayer).forEach(key => {
      if (initialPayer[key] != formValue[key]) {
        formHasChanged = true;
      }
    });

    return formHasChanged;
  }

  public handleInputChange(event) {
    if (this.payerHasUpdated()) {
      this.selectedFavorite = null;
    } else {
      this.selectedFavorite = this.payerForm.value;
    }
  }

  public handleClickSaveButton() {
    if (this.saveToFavorites && !this.selectedFavorite) {
      this.saveFavorite();
    }
    this.submitForm.emit();
  }

  public saveFavorite() {
    const payer: AccountPaymentFavoriteDTO = {
      codeBank: '',
      bankName: '',
      accountNumber: '',
      agencyNumber: '',
      typeAccount: '',
      type: TypeEnum.BILLET,
      uuidWhiteLabel: this.userProfile.uuidWhiteLabel,
      uuidAccountPayment: this.userProfile.uuidAccount,
      name: this.payerForm.value.name,
      document: this.payerForm.value.document,
      email: this.payerForm.value.email,
      zipCode: this.payerForm.value.postalCode,
      address: this.payerForm.value.address,
      addressNumber: this.payerForm.value.numberAddress,
      addressComplement: this.payerForm.value.complementAddress,
      neighborhood: this.payerForm.value.neighborhood,
      city: this.payerForm.value.cityAddress,
      state: this.payerForm.value.uf,
      country: this.payerForm.value.country,
    };

    this.accountPaymentFavoriteService
      .saveFavorite(payer)
      .pipe(
        tap((data) => {
          if (data.status) {}
        }),
        catchError((_) => {
          return of(null);
        }),
      )
      .subscribe();
  }

  public cancel() {
    this.onCancel.emit();
  }

}
